import React, { useContext, useEffect, useRef } from 'react'
import { View } from 'react-native'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { Divider } from '@ui-kitten/components'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Feature, TouchableSection, useFeatureFlag } from '@components'
import { Icon } from '@components/base'
import { AppStackParamList } from '@src/navigation/types'
import { NavigatorContext, useSnack } from '@src/utils'
import {
  EhrAppointmentAttendanceChargeKind,
  EhrAppointmentKind,
  EhrAppointmentStateKind,
} from '@src/types'
import { CustomEventTypes } from '@src/config'
import { appointmentKindSelector, userPropertiesSelector, userSelector } from '@selectors/app.ts'
import Storage from '@utils/storage.ts'
import { isAppointmentPastAccessWindow } from '../../utils'
import {
  appointmentRecurrenceSelector,
  ehrEarlierAvailableSlotsByAppointmentIdSelector,
  insurancePolicySelector,
} from '../../models/nutritionistHub.selectors'
import { VIDEO_CALL_DURATION_OPTIONS } from '../../constants'
import { useNutritionistHubTracker } from '../../hooks'
import { UpdateEhrAppointmentKindMutation } from '../../graphql/updateAppointmentKind.generated'
import { AppointmentDurationSelect } from '../AppointmentDurationSelect'
import { PaymentSummary } from '../CallScheduling/PaymentSummary'
import { VideoCallDetailsModal } from './VideoCallDetailsModal'
import { useAppointment } from './useAppointment'
import { InsuranceAuthorizationDetails } from './InsuranceAuthorizationDetails'
import { HealthCoachingCreditDetails } from './HealthCoachingCreditDetails'
import { InsuranceCoverageDetails } from './InsuranceCoverageDetails'
import { CancellationPolicy } from './CancellationPolicy'
import { AddToCalendarItem } from './AddToCalendarItem'
import { AppointmentDetails } from './AppointmentDetails'
import { VideoCallContentLoader } from './VideoCallContentLoader'

export const VideoCallModal = () => {
  const styles = useStyleSheet(themedStyles)
  const route = useRoute<RouteProp<AppStackParamList, 'VideoCallModal'>>()
  const navigation = useNavigation()
  const track = useNutritionistHubTracker()
  const showSnack = useSnack()
  const dispatch = useDispatch()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const updatingAppointmentKindRef = useRef(false)

  const user = useSelector(userSelector)
  const insurancePolicy = useSelector(insurancePolicySelector)
  const earlierAvailableSlotsByAppointmentId = useSelector(
    ehrEarlierAvailableSlotsByAppointmentIdSelector,
  )
  const { videoCallRescheduleFreePassUsed } = useSelector(userPropertiesSelector) ?? {}
  const appointmentRecurrence = useSelector(appointmentRecurrenceSelector)

  const earlierAvailableSlotsCheckFeatureEnabled = useFeatureFlag(
    Feature.VideoCallsEarlierAvailableSlotsCheck,
  )

  const { id } = route.params
  const { appointment, loading, error } = useAppointment({ id, skip: !id })
  const appointmentKindConfig = useSelector(appointmentKindSelector(appointment?.kind))

  useEffect(() => {
    if (!appointment) {
      return
    }

    if (isAppointmentPastAccessWindow(appointment.meetingAt)) {
      // Users can access details of past appointments if they open via a link
      // We should not show the modal in that case, to prevent any actions on past appointments
      // (e.g rescheduling or cancelling will create a fee)
      // So lets just show a snack and go back
      showSnack('This appointment has already passed.', null, 'error')
      navigation.goBack()
    }
  }, [appointment, navigation, showSnack])

  useEffect(
    () => () => {
      if (updatingAppointmentKindRef.current) {
        disableLoader()
        updatingAppointmentKindRef.current = false
      }
    },
    [disableLoader],
  )

  if (!appointment || !appointmentKindConfig || loading || error) {
    return (
      <VideoCallDetailsModal>
        <VideoCallContentLoader />
      </VideoCallDetailsModal>
    )
  }

  const { kind: appointmentKind, meetingAt: date } = appointment
  const attendance = appointment.attendances[0]
  const title = `${appointmentKindConfig.coachingKind.serviceTitle} Call`

  const nutritionistName = appointment.providerName
  const isCallWithin24h = moment(date).diff(moment(), 'hours') < 24
  const isCallWithin3Days = moment(date).diff(moment(), 'days') < 3

  const earlierAvailableSlotsForAppointment = earlierAvailableSlotsByAppointmentId[id] || []
  const appointmentHasEarlierAvailableSlots =
    earlierAvailableSlotsCheckFeatureEnabled && earlierAvailableSlotsForAppointment.length > 0

  const isFreeCall = attendance.chargeKind === EhrAppointmentAttendanceChargeKind.BsFree
  const isPrepaidCall = attendance.chargeKind === EhrAppointmentAttendanceChargeKind.BsPaid

  const checkInCompleted = Storage.get(`${Storage.VIDEO_CALL_CHECK_IN_COMPLETED_KEY}_${id}`, false)

  const canChangeAppointmentDuration =
    !isCallWithin24h &&
    insurancePolicy?.eligible &&
    VIDEO_CALL_DURATION_OPTIONS.some((option) => option.value === appointmentKind)

  const { lateCancellationFeeProduct, lateRescheduleFeeProduct } = appointment
  const { imposesLateChangeFees } = attendance
  const canModifyAppointment = [
    EhrAppointmentStateKind.Scheduled,
    EhrAppointmentStateKind.InReview,
  ].includes(appointment.lastState.kind)

  const handleReschedule = () => {
    track(CustomEventTypes.NutritionistHubRescheduleAppointmentTapped)

    if (!canModifyAppointment) {
      showSnack('This appointment can not be rescheduled.', null, 'error')
      navigation.goBack()
      return
    }

    const paidFeeInvoice = attendance.feeInvoice

    // User tried a late reschedule, paid the fee but something went wrong
    // with the rescheduling
    // Note that we previously used lateCancellationFeeProduct here only,
    // so, I'm checking both reschedule/cancellation kinds for backwards compatibility.
    if (
      paidFeeInvoice &&
      (paidFeeInvoice.primaryProduct.key === lateCancellationFeeProduct ||
        paidFeeInvoice.primaryProduct.key === lateRescheduleFeeProduct)
    ) {
      navigation.navigate('CallScheduling', {
        appointmentKind,
        appointmentId: id,
        allowBackNavigation: true,
      })
      return
    }

    if (isCallWithin24h && lateRescheduleFeeProduct && imposesLateChangeFees) {
      if (videoCallRescheduleFreePassUsed?.value === 'true') {
        navigation.navigate('LateRescheduleVideoCallModal', {
          videoCallId: id,
          appointmentKind,
          lateRescheduleFeeProduct,
          coachingKind: appointmentKindConfig.coachingKind.key,
        })
        return
      }

      navigation.navigate('FreePassLateRescheduleVideoCallModal', {
        videoCallId: id,
        appointmentKind,
        nutritionistName,
        lateRescheduleFeeProduct,
      })
      return
    }

    if (isPrepaidCall && isCallWithin24h && appointment.forbidLateReschedule) {
      navigation.navigate('LateCancelOrReschedulePaidVideoCallModal', {
        videoCallId: id,
        useRescheduleDescription: true,
      })
      return
    }

    if (appointmentHasEarlierAvailableSlots) {
      navigation.navigate('RescheduleVideoCallToEarlierSlot', {
        appointmentKind,
        appointmentId: id,
        nutritionistName,
        title,
        date,
      })
      return
    }

    navigation.navigate('CallScheduling', {
      appointmentKind,
      appointmentId: id,
      allowBackNavigation: true,
    })
  }

  const handleCancel = () => {
    track(CustomEventTypes.NutritionistHubCancelAppointmentTapped)

    if (!canModifyAppointment) {
      showSnack('This appointment can not be canceled.', null, 'error')
      navigation.goBack()
      return
    }

    if (isCallWithin24h && lateCancellationFeeProduct && imposesLateChangeFees) {
      if (videoCallRescheduleFreePassUsed?.value === 'true') {
        navigation.navigate('LateRescheduleOrCancelVideoCallModal', {
          videoCallId: id,
          appointmentKind,
          nutritionistName,
          lateCancellationFeeProduct,
        })
        return
      }

      navigation.navigate('FreePassLateRescheduleOrCancelVideoCallModal', {
        videoCallId: id,
        appointmentKind,
        nutritionistName,
        lateCancellationFeeProduct,
      })
      return
    }

    if (isPrepaidCall && isCallWithin24h) {
      navigation.navigate('LateCancelOrReschedulePaidVideoCallModal', { videoCallId: id })
      return
    }

    navigation.navigate('RescheduleOrCancelVideoCallModal', {
      videoCallId: id,
      appointmentDate: date,
      appointmentKind,
      nutritionistName,
      recurringAppointment: appointment.recurring,
    })
  }

  const handleAppointmentKindChange = (option: { value: EhrAppointmentKind }) => {
    if (option.value === appointmentKind) {
      return
    }

    if (updatingAppointmentKindRef.current) {
      return
    }

    updatingAppointmentKindRef.current = true
    enableLoader()

    track(CustomEventTypes.NutritionistHubAppointmentDurationChanged, {
      appointmentKind: option.value,
    })

    dispatch({
      type: 'nutritionistHub/updateAppointmentKind',
      payload: {
        id,
        appointmentKind: option.value,
      },
      success: (response: UpdateEhrAppointmentKindMutation['updateEhrAppointmentKind']) => {
        navigation.setParams({
          title: response.title || title,
          appointmentKind: response.kind,
        })
      },
      failure: (error: any) => {
        const errorMessage = error?.message || 'Failed to update appointment kind'
        showSnack(errorMessage, null, 'error')
      },
      complete: () => {
        disableLoader()
        updatingAppointmentKindRef.current = false
      },
    })
  }

  return (
    <VideoCallDetailsModal>
      <AppointmentDetails
        title={title}
        providerName={appointment.providerName}
        date={date}
        chargeKind={attendance.chargeKind}
        appointmentState={appointment.lastState.kind}
      />

      <InsuranceAuthorizationDetails chargeKind={attendance.chargeKind} />
      <HealthCoachingCreditDetails />
      <CancellationPolicy
        imposesLateChangeFees={attendance.imposesLateChangeFees}
        lateCancellationFeeProduct={lateCancellationFeeProduct}
      />

      <Divider appearance="large" style={styles.divider} />

      <InsuranceCoverageDetails
        chargeKind={attendance.chargeKind}
        appointmentState={appointment.lastState.kind}
      />

      <View style={styles.actionOptionsContainer}>
        {isCallWithin3Days && (
          <TouchableSection
            onPress={() => {
              const screen = checkInCompleted ? 'VideoCallCheckInAddendum' : 'VideoCallCheckIn'
              navigation.navigate(screen, { appointmentId: id })
            }}
            iconName="paper-plane-tilt"
            title={
              checkInCompleted ? 'Add extra information to Call Check In' : 'Complete Call Check In'
            }
            style={styles.actionOption}
            labelStyle={styles.actionOptionText}
          />
        )}

        <AddToCalendarItem
          addToGoogleCalendarLink={appointment.addToGcalLink}
          unauthenticatedIcsLink={appointment.unauthenticatedIcsLink}
        />

        <TouchableSection
          onPress={handleReschedule}
          iconName="pencil"
          title="Reschedule Appointment"
          subtitle={appointmentHasEarlierAvailableSlots ? 'Earlier slots available' : undefined}
          subtitleStyle={styles.link}
          style={styles.actionOption}
          labelStyle={styles.actionOptionText}
        />

        {appointment.recurring && appointmentRecurrence && (
          <TouchableSection
            onPress={() =>
              navigation.navigate('UpsertRecurrence', { appointmentRecurrence, insurancePolicy })
            }
            iconName="pencil"
            title="Edit Recurring Appointment"
            subtitleStyle={styles.link}
            style={styles.actionOption}
            labelStyle={styles.actionOptionText}
          />
        )}

        {canChangeAppointmentDuration && (
          <TouchableSection
            iconName="clock"
            title="Set Call Duration"
            subtitle="You can change it up to 24 hours before the call"
            subtitleStyle={styles.secondarySubtitle}
            style={styles.actionOption}
            labelStyle={styles.actionOptionText}
          >
            <AppointmentDurationSelect
              handleSelectChange={handleAppointmentKindChange}
              selectedAppointmentKind={appointmentKind}
            />
          </TouchableSection>
        )}

        <TouchableSection
          onPress={handleCancel}
          accessoryLeft={<Icon name="x" style={styles.actionOptionCancelIcon} />}
          title="Cancel Appointment"
          style={styles.actionOption}
          labelStyle={styles.actionOptionCancelText}
        />
      </View>

      <Divider appearance="large" style={styles.divider} />

      {!isFreeCall && (
        <PaymentSummary
          attendanceChargeKind={attendance.chargeKind}
          date={date}
          product={appointment.payAsYouGoProduct}
          insurancePolicy={insurancePolicy}
          paymentMethod={user?.paymentMethod}
          onlyShowDateCharged
        />
      )}
    </VideoCallDetailsModal>
  )
}

const themedStyles = StyleService.create({
  link: {
    color: 'theme.text.link',
  },
  actionOptionsContainer: {
    marginHorizontal: 8,
  },
  actionOption: {
    paddingHorizontal: 0,
  },
  actionOptionText: {
    color: 'theme.text.primary',
    fontWeight: '400',
  },
  actionOptionCancelText: {
    color: 'theme.range.bad',
    fontWeight: '400',
  },
  actionOptionCancelIcon: {
    tintColor: 'theme.range.bad',
    height: 24,
    width: 24,
    marginRight: 12,
  },
  secondarySubtitle: {
    color: 'theme.text.secondary',
  },
  divider: {
    marginHorizontal: -16,
    marginVertical: 8,
  },
})
